import type { ReactNode } from 'react';

import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Panel, PanelContent } from '@/components/ui/panel';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Variant types
// ---------------------------------------------------------------------------

/**
 * Visual scale of an empty state. Drives padding, icon treatment, and title
 * typography so the dozen+ surfaces that show "nothing here yet" read as one
 * system instead of a dozen hand-rolled layouts.
 *
 * - `page` — full-route placeholder (e.g. an empty markets / launchpad feed):
 *   large faded icon, `text-heading`, generous padding.
 * - `section` — a panel body or tab placeholder (the default): a circular
 *   tinted icon badge, `text-heading-md`, medium padding.
 * - `compact` — an inline list slot (leaderboard tab, traders panel, sessions
 *   list): small bare icon, `text-body`, tight padding.
 */
export type EmptyStateSize = 'page' | 'section' | 'compact';

export interface EmptyStateProps {
  /** Glyph shown above the title. Omit for a text-only empty state. */
  icon?: IconSvgElement | undefined;
  /** Headline — the primary "nothing here" message. Pass already-translated copy. */
  title: string;
  /** Supporting copy below the title. Pass already-translated copy. */
  description?: ReactNode;
  /** Visual scale. @default 'section' */
  size?: EmptyStateSize | undefined;
  /** Wrap the content in a `<Panel>` surface. @default false */
  framed?: boolean | undefined;
  /**
   * Primary call-to-action. Pass an `<AnimatedButton>` (optionally `asChild`
   * wrapping a `<Link>`). Omit for an empty state with no action.
   */
  action?: ReactNode;
  /**
   * Extra content rendered below the action — ramp steps, a secondary CTA, or
   * a mounted dialog. Reserved for the bespoke cases the slots above don't cover.
   */
  children?: ReactNode;
  /** Layout passthrough (positioning / width). Not for design tokens. */
  className?: string | undefined;
  'data-testid'?: string | undefined;
}

// ---------------------------------------------------------------------------
// Token tables (module-level — React Compiler friendly)
// ---------------------------------------------------------------------------

const LAYOUT_BASE = 'flex flex-col items-center justify-center text-center' as const;

const CONTAINER_SIZE: Record<EmptyStateSize, string> = {
  page: 'gap-4 px-4 py-24',
  section: 'gap-3 py-12',
  compact: 'gap-3 py-8',
};

const TITLE_SIZE: Record<EmptyStateSize, string> = {
  page: 'text-heading',
  section: 'text-heading-md',
  compact: 'text-body font-medium text-foreground-primary',
};

const DESCRIPTION_SIZE: Record<EmptyStateSize, string> = {
  page: 'max-w-md text-body text-foreground-secondary',
  section: 'max-w-sm text-body text-foreground-secondary',
  compact: 'prose-width text-caption text-foreground-secondary',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * EmptyState — the canonical "this list / section / page has no data" surface.
 *
 * Replaces ~a dozen ad-hoc empty-state layouts (markets, launchpad, portfolio,
 * leaderboard, market panels…) that each diverged on icon size, title
 * typography, and padding. Presentational only: callers pass already-translated
 * `title` / `description` and the `action` node, so the primitive stays
 * decoupled from any i18n namespace.
 *
 * ```tsx
 * <EmptyState
 *   framed
 *   icon={ChartBarLineIcon}
 *   title={t('title')}
 *   description={t('body')}
 *   action={
 *     <AnimatedButton asChild>
 *       <Link href="/markets">{t('cta')}</Link>
 *     </AnimatedButton>
 *   }
 * />
 * ```
 */
export function EmptyState({
  icon,
  title,
  description,
  size = 'section',
  framed = false,
  action,
  children,
  className,
  'data-testid': testId,
}: EmptyStateProps): React.ReactNode {
  const body: ReactNode = (
    <>
      {icon !== undefined ? <EmptyStateIcon icon={icon} size={size} /> : null}
      <EmptyStateTitle size={size}>{title}</EmptyStateTitle>
      {description !== undefined && description !== null ? (
        <p className={DESCRIPTION_SIZE[size]}>{description}</p>
      ) : null}
      {action}
      {children}
    </>
  );

  if (framed) {
    return (
      <Panel>
        <PanelContent
          className={cn(LAYOUT_BASE, CONTAINER_SIZE[size], className)}
          data-testid={testId}
        >
          {body}
        </PanelContent>
      </Panel>
    );
  }

  return (
    <div className={cn(LAYOUT_BASE, CONTAINER_SIZE[size], className)} data-testid={testId}>
      {body}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Private parts
// ---------------------------------------------------------------------------

function EmptyStateTitle({
  size,
  children,
}: {
  size: EmptyStateSize;
  children: ReactNode;
}): React.ReactNode {
  // `page`/`section` are standalone landmarks → real headings; `compact` lives
  // inside dense list/tab contexts that already own the heading level, so it
  // stays a paragraph to avoid injecting a stray heading into the outline.
  if (size === 'compact') {
    return <p className={TITLE_SIZE.compact}>{children}</p>;
  }
  const Tag = size === 'page' ? 'h2' : 'h3';
  return <Tag className={TITLE_SIZE[size]}>{children}</Tag>;
}

function EmptyStateIcon({
  icon,
  size,
}: {
  icon: IconSvgElement;
  size: EmptyStateSize;
}): React.ReactNode {
  if (size === 'section') {
    // Circular tinted badge — the more "finished" treatment for framed panels.
    return (
      <div className="flex size-12 items-center justify-center rounded-full bg-background-tertiary">
        <Icon icon={icon} className="size-6 text-foreground-secondary" aria-hidden="true" />
      </div>
    );
  }
  if (size === 'page') {
    // Solid tertiary, not a transparent secondary: opacity made the icon's
    // overlapping strokes (e.g. the search-off magnifier) read as doubled
    // ghost shapes (MOR-823).
    return <Icon icon={icon} className="size-16 text-foreground-tertiary" aria-hidden="true" />;
  }
  return <Icon icon={icon} className="size-10 text-foreground-secondary" aria-hidden="true" />;
}
